import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';

import { cloneDeep, isNil, size } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { useTheme } from "@mui/material/styles";

// Hooks
import useAuthScope from 'hooks/useAuthScope';

import {
  Box,
  Button,
  Typography,
  Collapse,
  TextField,
  Tooltip,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { ReactComponent as ProIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";
import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as CheckDisabledIcon } from "assets/images/icons/checkbox_disabled.svg";
import icon_snmp from 'assets/images/icons/icn_Info_solid.svg';
import icon_snmp_hover from 'assets/images/icons/icn_info_solid_hover.svg';

import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import LinkModal from 'components/LinkModal';

import SelectionDropdownProfile from '../../components/SelectionDropdownProfile';

// Global const
import { passwordRegex } from 'const';

// Local const
import { defaultFormData, SECURITY_ALLOWED_6G,  SECURITY_ALLOWED_MLO, PMF_MAP, formLimitation, isGroupKeyVisible } from "../../const";

const CreateNewWirelessNetworkModal = ({
  isUnityPro,
  setAlertInfo,
  wirelessData,
  setWirelessData,
  onUpdateBlockerData,
  bandOptions,
  openCreateModal,
  setOpenCreateModal,
  securityTypeAssembleList,
  securityTypeConfig,
  isValidInputStyles,
  radiusProfileOptions,
  wirelessScheduleOptions,
  macFilterOptions,
  ipFilterOptions,
  captivePortalOptions,
  bandCount,
  onCreateNewProfile,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { auth } = useAuthScope();

  const [formData, setFormData] = useState(cloneDeep(defaultFormData));
  const [hasDuplicateName, setHasDuplicateName] = useState(false);

  const [securityList, setSecurityList] = useState(
    structuredClone(securityTypeAssembleList)
  );
  const [openGeneral, setOpenGeneral] = useState(true);
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(true);
  const [isLoading, _setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const ssidNameSize = useMemo(() => size(formData?.ssid?.trim()), [size, formData?.ssid]);

  const showPasswordSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isPasswordRequired;
  }, [formData?.security]);

  const showRadiusProfileSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isRadiusProfileRequired;
  }, [formData?.security]);

  const isPasswordValid = useMemo(() => {
    let isValid = true;

    if (!formData) return isValid;

    const isPasswordRequiredSecurityMode =
      securityTypeConfig[formData?.security]?.isPasswordRequired;
    const isPasswordValidByRegexp = passwordRegex.wireless.ssid.test(formData?.password);

    if (isPasswordRequiredSecurityMode && !isPasswordValidByRegexp)
      isValid = false;
    else if (
      !isPasswordRequiredSecurityMode &&
      formData?.password &&
      !isPasswordValidByRegexp
    )
      isValid = false;

    return isValid;
  }, [formData?.security, formData?.password]);

  const handleCloseModal = () => {
    setOpenCreateModal(false);
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSave = () => {
    const tmpCreateId = uuidv4();
    const newWirelessData = [ ...wirelessData, { ...formData, isWaitingCreate: true, tmpCreateId }];

    const finalWirelessData = structuredClone(newWirelessData);
    setWirelessData(finalWirelessData);
    onUpdateBlockerData(finalWirelessData);

    setOpenCreateModal(false);
    setShowPassword(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const checkSaveProRule = () => {
    const basicTypeArray = ['disabled', 'CLICK_THROUGH', 'USERNAME_PASSWORD'];
    const selectedAuthType = captivePortalOptions?.find(item => item?.value === formData?.captivePortalName)?.type || 'disabled';

    if (
      (!isUnityPro && formData?.rateLimit !== 'DISABLE') ||
      (!isUnityPro && formData?.macFilter !== 'disabled') ||
      (!isUnityPro && formData?.ipFilter !== 'disabled') ||
      (!isUnityPro && !basicTypeArray.includes(selectedAuthType))
    ) {
      setIsLinkModalOpen(true);
      return; // Open the link modal first.
    }

    onSave();
  };

  const isFormInvalid = () => {
    let isInvalid = false;

    if (!formData?.ssid || ssidNameSize < 1 || ssidNameSize > 32 || hasDuplicateName) isInvalid = true;
    if (!formData?.band?.length) isInvalid = true;
    if (!formData?.security) isInvalid = true;
    if (!formData?.vlanId || formData?.vlanId < formLimitation.vlanId.min || formData?.vlanId > formLimitation.vlanId.max) isInvalid = true;
    if (showPasswordSetting && !isPasswordValid) isInvalid = true;

    if (isGroupKeyVisible(formData?.security)) {
      if (
        !formData?.groupKeyUpdatePeriod ||
        formData?.groupKeyUpdatePeriod < formLimitation.groupKeyUpdatePeriod.min ||
        formData?.groupKeyUpdatePeriod > formLimitation.groupKeyUpdatePeriod.max
      ) {
        isInvalid = true;
      }
    }

    if (formData?.rateLimit === 'PER_SSID' || formData?.rateLimit === 'PER_CLIENT') {
      if (!formData?.rateLimitDownload || formData?.rateLimitDownload < formLimitation.rateLimitDownload.min || formData?.rateLimitDownload > formLimitation.rateLimitDownload.max) {
        isInvalid = true;
      }
      if (!formData?.rateLimitUpload || formData?.rateLimitUpload < formLimitation.rateLimitUpload.min || formData?.rateLimitUpload > formLimitation.rateLimitUpload.max) {
        isInvalid = true;
      }
    }

    const isSupportRadiusProfile = ["WPA/WPA2-ENTERPRISE", "WPA2-ENTERPRISE", "WPA3-ENTERPRISE"].includes(formData.security);
    const hasRadiusProfile = radiusProfileOptions.find(item => item?.value === formData?.radiusProfile);
    if (isSupportRadiusProfile && !hasRadiusProfile) {
      isInvalid = true;
    }

    return isInvalid;
  };

  const textfieldStyle = {
    width: "286px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "30px",
      "& fieldset": {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.mainHover,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    // Hide native number spinners (WebKit)
    '& .MuiOutlinedInput-root input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-root input[type="number"]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    // Firefox / generic appearance
    '& .MuiOutlinedInput-root input[type="number"]': {
      MozAppearance: 'textfield',
      appearance: 'textfield',
    },
  };

  const rowStyle = {
    paddingLeft: "32px",
    marginTop: "20px",
    display: "flex",
    alignItems: "center",
    gap: 2,
  };

  const labelStyle = {
    width: "180px",
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const readOnlyStyle = {
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[1],
  };

  const getAvailableSecurities = useCallback(({ bands }) => {
    const has6G = bands.includes('6');

    if (bands.length === 0) {
      return securityTypeAssembleList;
    }

    if (has6G) {
      return securityTypeAssembleList.filter(s => SECURITY_ALLOWED_6G.includes(s.value));
    }

    return securityTypeAssembleList;
  }, []);

  useEffect(() => {
    const bands = formData?.band ?? [];
    const isMultipleBands = bands.length >= 2;
    const newSecurityList = getAvailableSecurities({ bands });
    setSecurityList(newSecurityList);

    setFormData(prev => {
      const updates = {};

      // Ensure mlo is disabled when multiple bands are not selected.
      if (!isMultipleBands) {
        updates.mloEnable = false;
      }

      // Ensure selected security is valid for the current band configuration.
      const securityValid = newSecurityList.some(s => s.value === prev.security);
      const nextSecurity = securityValid ? prev.security : newSecurityList[0]?.value ?? null;
      if (nextSecurity !== prev.security) {
        updates.security = nextSecurity;
      }

      // Disable MLO when security does not support it.
      const mloAllowed = SECURITY_ALLOWED_MLO.includes(nextSecurity);
      if (!mloAllowed && prev.mloEnable) {
        updates.mloEnable = false;
      }

      // Update wpaMode only when security changes.
      if (nextSecurity && nextSecurity !== prev.security) {
        const config = securityTypeConfig[nextSecurity];
        updates.wpaMode = config?.wpaMode?.display ?? null;
        updates.encryption = config?.encryption ?? null;
        updates.password = config?.isPasswordRequired ? prev.password : null;
        updates.groupKeyUpdatePeriod = isGroupKeyVisible(nextSecurity) ? prev.groupKeyUpdatePeriod : 3600;
      }

      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [formData?.band, formData?.security]);

  useEffect(() => {
    const newPmfState = PMF_MAP[formData?.wpaMode] ?? 'Disable';
    if (formData.pmfState !== newPmfState) {
      setFormData(prev => ({ ...prev, pmfState: newPmfState }));
    }
  }, [formData?.wpaMode]);

  useEffect(() => {
    if (!openCreateModal) {
      setFormData(cloneDeep(defaultFormData));
      setSecurityList(structuredClone(securityTypeAssembleList));
      setShowPassword(false);
      setHasDuplicateName(false);
      setOpenGeneral(true);
      setOpenAdvancedSettings(true);
    }
  }, [openCreateModal]);

  return (
    <>
      <ModalContainer display={openCreateModal} modalWidth="850px">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
          </Box>
        )}
        <Box sx={{ padding: "0 10px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Create New Wireless Network
            </Typography>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: theme.palette.primary.transparent,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.palette.primary.transparent,
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "40px",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                transform: openGeneral ? "rotate(90deg)" : "rotate(0deg)",
                cursor: "pointer",
              }}
              onClick={() => setOpenGeneral(!openGeneral)}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              General
            </Typography>
          </Box>

          <Collapse in={openGeneral}>
            {/* Wi-Fi Name (SSID) */}
            <Box sx={{ ...rowStyle, marginBottom: hasDuplicateName ? '10px' : '0px' }}>
              <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '10px', height: '44px' }}>Wi-Fi Name (SSID)</Box>
              <TextField
                placeholder="e.g. My Network"
                sx={textfieldStyle}
                autoComplete="off"
                value={formData.ssid || ''}
                onChange={e => {
                  setFormData((prev) => ({ ...prev, ssid: e.target.value }))
                  setHasDuplicateName(wirelessData?.some(item => item?.ssid === e.target.value));
                }}
                error={hasDuplicateName || (!isNil(formData?.ssid) && ( ssidNameSize < 1 || ssidNameSize > 32 ))}
                helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
              />
            </Box>
            {/* Band */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Band</Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {Object.keys(bandOptions).map((band, index) => (
                  <Box
                    key={band}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      sx={{ paddingLeft: index === 0 ? '0px' : '9px' }}
                      checked={!!formData?.band?.includes(band)}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const newBand = (
                            e.target.checked
                              ? [...prev.band, band]
                              : prev.band.filter((v) => v !== band)
                          ).sort((a, b) => a - b);

                          return { ...prev, band: newBand };
                        })
                      }
                      icon={bandCount?.[band] >= 8 ? <CheckDisabledIcon style={{ width: 24, height: 24, paddingTop: 2 }} /> : <UnselectedIcon />}
                      checkedIcon={<SelectedIcon />}
                      disableRipple
                      disabled={bandCount?.[band] >= 8}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                      }}
                    >
                      {bandOptions[band]} GHz
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* MLO */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, display: 'flex' }}>
                MLO
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: '455px',
                        backgroundColor: '#4D4D4D',
                      },
                    },
                  }}
                  title={
                    <Box>
                      <Typography variant="body2" fontSize={13}>
                        Enable Multi-Link Operation (MLO) to let Wi-Fi 7 clients connect across multiple bands and switch smoothly between links for improved performance and
                        reliability.
                      </Typography>
                      <Typography fontWeight="bold" fontSize={13}>
                        Important
                      </Typography>
                      <Typography variant="body2" fontSize={13}>
                        MLO requires Wi-Fi 7 (802.11be) and MLO-capable client devices.
                      </Typography>
                      <Typography variant="body2" fontSize={13}>
                        The SSID enabled MLO only supports WPA3 or Enhanced Open clients.
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 16,
                      marginLeft: 1,
                      '& img': {
                        content: `url(${icon_snmp})`,
                      },
                      '&:hover img': {
                        content: `url(${icon_snmp_hover})`,
                      },
                    }}
                  >
                    <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </Box>
                </Tooltip>
              </Box>
              <Box
                sx={{ width: 40, height: 22, display: 'flex', alignItems: 'center' }}
                onClick={() => setFormData(prev => ({ ...prev, mloEnable: !prev?.mloEnable }))}
              >
                <IconButton
                  sx={{
                    paddingLeft: 0,
                    cursor: (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) ? 'default' : 'pointer',
                  }}
                  onClick={(e) => (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) && e.stopPropagation()}
                >
                  {formData?.mloEnable ? <SwitchOnIcon /> : <SwitchOffIcon />}
                </IconButton>
              </Box>
            </Box>
            {/* Security */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Security</Box>
              <MenuSelect
                value={formData?.security || ''}
                onChange={(opt) => {
                  const isOpenType = [
                    "OPEN",
                    "ENHANCE_OPEN",
                    "ENHANCE_OPEN+OPEN",
                  ].includes(opt.value);
                  const config = securityTypeConfig[opt.value] || {
                    wpaMode: "",
                    encryption: "",
                  };
                  setFormData((prev) => ({
                    ...prev,
                    security: opt.value,
                    wpaMode: config.wpaMode.display,
                    encryption: config.encryption,
                    password: config.isPasswordRequired ? prev.password : null,
                    radiusProfile: config.isRadiusProfileRequired ? radiusProfileOptions?.[0]?.value : null,
                    groupKeyUpdatePeriod: isOpenType ? 3600 : prev.groupKeyUpdatePeriod,
                  }));
                }}
                options={securityList.filter((security) => security.isShow)}
                width={textfieldStyle.width}
              />
            </Box>
            {/* RADIUS Profile */}
            {showRadiusProfileSetting && (
              <Box sx={rowStyle}>
                <Box sx={labelStyle}>RADIUS Profile</Box>
                <SelectionDropdownProfile
                  menuList={radiusProfileOptions?.length > 0 ? radiusProfileOptions : []}
                  value={formData?.radiusProfile ?? ''}
                  width={textfieldStyle.width}
                  onChangeItem={(opt) => {
                    setFormData((prev) => ({
                      ...prev,
                      radiusProfile: opt?.value ?? 'disabled',
                    }));
                  }}
                  onCreateNewProfile={() => onCreateNewProfile({ profileType: 'RADIUS Profile', page: 'radius-server' })}
                />
              </Box>
            )}
            {/* PMF */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>PMF</Box>
              <Typography sx={readOnlyStyle}>{formData?.pmfState || 'Disable'}</Typography>
            </Box>
            {/* WPA Mode */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>WPA Mode</Box>
              <Typography sx={readOnlyStyle}>
                {formData.wpaMode || "-"}
              </Typography>
            </Box>
            {/* Encryption Type */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Encryption Type</Box>
              <Typography sx={readOnlyStyle}>
                {formData.encryption || "-"}
              </Typography>
            </Box>
            {/* Wi-Fi Password */}
            {showPasswordSetting && (
              <>
                <Box sx={rowStyle}>
                  <Box sx={labelStyle}>Wi-Fi Password</Box>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    sx={{
                      ...textfieldStyle,
                      ...isValidInputStyles(
                        formData?.password == null || isPasswordValid
                      ),
                      width: textfieldStyle.width,
                    }}
                    autoComplete="off"
                    value={formData.password || ''}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }));
                    }}
                    placeholder="Enter Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleShowPassword}
                            edge="end"
                            sx={{
                              padding: 1,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                            }}
                          >
                            {showPassword ? <ShowIcon /> : <HideIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </>
            )}

            {showPasswordSetting && !!formData.password && !isPasswordValid && (
              <Box
                sx={{
                  ...rowStyle,
                  marginTop: "3px",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.primary.redPrimary,
                    fontSize: "0.75rem",
                    marginLeft: "12.3rem",
                  }}
                >
                  Minimum length: 8 characters. Allowed symbols: ~ ! [ ] ; ' \ : @ # $ & *
                </Typography>
              </Box>
            )}

            <Box sx={rowStyle}>
              <Box sx={labelStyle}>VLAN</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  type="number"
                  autoComplete="off"
                  value={formData.vlanId || ""}
                  sx={{
                    ...textfieldStyle,
                    ...isValidInputStyles(
                      formData?.vlanId === defaultFormData.vlanId ||
                      (formData?.vlanId >= formLimitation.vlanId.min && formData?.vlanId <= formLimitation.vlanId.max)
                    ),
                    width: textfieldStyle.width,
                  }}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vlanId: +e.target.value,
                    }))
                  }
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  {/* (1~4094) */}
                  {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                </Typography>
              </Box>
            </Box>
          </Collapse>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "40px",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: openAdvancedSettings ? 'rotate(90deg)' : 'rotate(0deg)',
                cursor: 'pointer',
              }}
              onClick={() => setOpenAdvancedSettings(!openAdvancedSettings)}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Advanced Settings
            </Typography>
          </Box>

          <Collapse in={openAdvancedSettings}>
            {/* SSID Broadcast */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>SSID Broadcast</Box>
              <Box
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    broadcastSSID: !prev.broadcastSSID,
                  }))
                }
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: 40,
                  height: 22,
                }}
              >
                {formData.broadcastSSID ? <SwitchOnIcon /> : <SwitchOffIcon />}
              </Box>
            </Box>

            {/* Group Key Update Period */}
            {isGroupKeyVisible(formData?.security) && (
              <Box sx={rowStyle}>
                <Box sx={labelStyle}>Group Key Update Period</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    type="number"
                    autoComplete="off"
                    value={formData.groupKeyUpdatePeriod || ''}
                    sx={{
                      ...textfieldStyle,
                      ...isValidInputStyles(
                        formData?.groupKeyUpdatePeriod === defaultFormData.groupKeyUpdatePeriod ||
                        (formData?.groupKeyUpdatePeriod >= formLimitation.groupKeyUpdatePeriod.min && formData?.groupKeyUpdatePeriod <= formLimitation.groupKeyUpdatePeriod.max)
                      ),
                      width: textfieldStyle.width,
                    }}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        groupKeyUpdatePeriod: +e.target.value,
                      }))
                    }
                  />
                  <Typography
                    sx={{
                      color: theme.palette.primary.greySecound,
                      fontSize: theme.typography.customFontSize[1],
                    }}
                  >
                    {/* (300~86400) */}
                    {`(${formLimitation.groupKeyUpdatePeriod.min}~${formLimitation.groupKeyUpdatePeriod.max})`}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Fast Roaming */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Fast Roaming</Box>
              <Box
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    fastRoaming: !prev.fastRoaming,
                  }))
                }
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: 40,
                  height: 22,
                }}
              >
                {formData.fastRoaming ? <SwitchOnIcon /> : <SwitchOffIcon />}
              </Box>
            </Box>

            {/* Client Isolation */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, display: 'flex' }}>
                Client Isolation
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: '405px',
                        backgroundColor: '#4D4D4D',
                      },
                    },
                  }}
                  title={
                    <Box>
                      <Typography variant="body2" fontSize={13}>
                        Isolate the traffic between Wi-Fi clients within an Access Point. (Client Isolation + WLAN Partition)
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 16,
                      marginLeft: 1,
                      '& img': {
                        content: `url(${icon_snmp})`,
                      },
                      '&:hover img': {
                        content: `url(${icon_snmp_hover})`,
                      },
                    }}
                  >
                    <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </Box>
                </Tooltip>
              </Box>
              <Box
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    clientIsolation: !prev.clientIsolation,
                  }))
                }
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: 40,
                  height: 22,
                }}
              >
                {formData.clientIsolation ? <SwitchOnIcon /> : <SwitchOffIcon />}
              </Box>
            </Box>

            {/* Wireless Schedule */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Wireless Schedule</Box>
              <SelectionDropdownProfile
                menuList={wirelessScheduleOptions?.length > 0 ? wirelessScheduleOptions : [{ label: 'Disabled', value: 'disabled' }]}
                value={formData?.wirelessSchedule ?? 'disabled'}
                width={textfieldStyle.width}
                onChangeItem={(opt) => {
                  setFormData((prev) => ({
                    ...prev,
                    wirelessSchedule: opt?.value ?? 'disabled',
                  }));
                }}
                onCreateNewProfile={() => onCreateNewProfile({ profileType: 'Schedule', page: 'schedule' })}
              />
            </Box>

            {/* Rate Limit */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box>Rate Limit</Box>
                {formData?.rateLimit === 'DISABLE' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ProIcon />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isUnityPro ? (
                      <ProIcon />
                    ) : (
                      <Tooltip
                        title="Pro feature disabled."
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#4D4D4D', color: '#ffffff', px: 1.5, py: 0.5, borderRadius: '6px' } },
                          arrow: { sx: { color: '#4D4D4D' } },
                        }}
                      >
                        <ProOrangeOutlineIcon />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <Box>
                <Box style={{ display: 'flex', marginBottom: 5 }}>
                  <Box style={{ width: 100 }}>
                    <RadioButton
                      label="Disabled"
                      name="rateLimit"
                      value={'DISABLE'}
                      checked={formData?.rateLimit === 'DISABLE'}
                      onChange={() => setFormData(prev => ({ ...prev, rateLimit: 'DISABLE', rateLimitDownload: null, rateLimitUpload: null }))}
                    />
                  </Box>
                  <Box style={{ width: 105 }}>
                    <RadioButton
                      label="Per SSID"
                      name="rateLimit"
                      value={'PER_SSID'}
                      checked={formData?.rateLimit === 'PER_SSID'}
                      onChange={() => setFormData(prev => ({ ...prev, rateLimit: 'PER_SSID' }))}
                    />
                  </Box>
                  <Box style={{ width: 110 }}>
                    <RadioButton
                      label="Per Client"
                      name="rateLimit"
                      value={'PER_CLIENT'}
                      checked={formData?.rateLimit === 'PER_CLIENT'}
                      onChange={() => setFormData(prev => ({ ...prev, rateLimit: 'PER_CLIENT' }))}
                    />
                  </Box>
                </Box>

                {(formData?.rateLimit === 'PER_SSID' || formData?.rateLimit === 'PER_CLIENT') && (
                  <Box>
                    {/* Download Limit */}
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <Typography sx={{ width: 120, fontSize: theme.typography.customFontSize[2], }}>Download Limit</Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 70,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Mbps
                      </Box>
                      <TextField
                        sx={{...textfieldStyle, marginRight: 1 }}
                        autoComplete="off"
                        type="number"
                        min={formLimitation.rateLimitDownload.min}
                        max={formLimitation.rateLimitDownload.max}
                        value={formData?.rateLimitDownload || ''}
                        onChange={e => setFormData(prev => ({ ...prev, rateLimitDownload: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={!isNil(formData?.rateLimitDownload) && ( formData?.rateLimitDownload < formLimitation.rateLimitDownload.min || formData?.rateLimitDownload > formLimitation.rateLimitDownload.max )}
                      />
                      <Typography
                        sx={{
                          color: theme.palette.primary.greySecound,
                          fontSize: theme.typography.customFontSize[1],
                        }}
                      >
                        {/* (1~2560) */}
                        {`(${formLimitation.rateLimitDownload.min}~${formLimitation.rateLimitDownload.max})`}
                      </Typography>
                    </Box>
                    {/* Upload Limit */}
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', marginTop: 2 }}>
                      <Typography sx={{ width: 120, fontSize: theme.typography.customFontSize[2], }}>Upload Limit</Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 70,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Mbps
                      </Box>
                      <TextField
                        sx={{...textfieldStyle, marginRight: 1 }}
                        autoComplete="off"
                        type="number"
                        min={formLimitation.rateLimitUpload.min}
                        max={formLimitation.rateLimitUpload.max}
                        value={formData?.rateLimitUpload || ''}
                        onChange={e => setFormData(prev => ({ ...prev, rateLimitUpload: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={!isNil(formData?.rateLimitUpload) && ( formData?.rateLimitUpload < formLimitation.rateLimitUpload.min || formData?.rateLimitUpload > formLimitation.rateLimitUpload.max )}
                      />
                      <Typography
                        sx={{
                          color: theme.palette.primary.greySecound,
                          fontSize: theme.typography.customFontSize[1],
                        }}
                      >
                        {/* (1~2560) */}
                        {`(${formLimitation.rateLimitUpload.min}~${formLimitation.rateLimitUpload.max})`}
                      </Typography>
                    </Box>
                  </Box>
                )}

              </Box>
            </Box>

            {/* MAC Filter */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box>MAC Filter (Deny)</Box>
                {formData?.macFilter === 'disabled' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                    <ProIcon />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                    {isUnityPro ? (
                      <ProIcon />
                    ) : (
                      <Tooltip
                        title="Pro feature disabled."
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#4D4D4D', color: '#ffffff', px: 1.5, py: 0.5, borderRadius: '6px' } },
                          arrow: { sx: { color: '#4D4D4D' } },
                        }}
                      >
                        <ProOrangeOutlineIcon />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <SelectionDropdownProfile
                menuList={macFilterOptions?.length > 0 ? macFilterOptions : [{ label: 'Disabled', value: 'disabled' }]}
                value={formData?.macFilter ?? 'disabled'}
                width={textfieldStyle.width}
                onChangeItem={(opt) => {
                  setFormData((prev) => ({
                    ...prev,
                    macFilter: opt?.value ?? 'disabled',
                  }));
                }}
                onCreateNewProfile={() => onCreateNewProfile({ profileType: 'MAC Address Group', page: 'mac-address' })}
              />
            </Box>

            {/* IP Filter */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box>IP Filter (Allow)</Box>
                {formData?.ipFilter === 'disabled' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                    <ProIcon />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                    {isUnityPro ? (
                      <ProIcon />
                    ) : (
                      <Tooltip
                        title="Pro feature disabled."
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#4D4D4D', color: '#ffffff', px: 1.5, py: 0.5, borderRadius: '6px' } },
                          arrow: { sx: { color: '#4D4D4D' } },
                        }}
                      >
                        <ProOrangeOutlineIcon />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SelectionDropdownProfile
                  menuList={ipFilterOptions?.length > 0 ? ipFilterOptions : [{ label: 'Disabled', value: 'disabled' }]}
                  value={formData?.ipFilter ?? 'disabled'}
                  width={textfieldStyle.width}
                  onChangeItem={(opt) => {
                    setFormData((prev) => ({
                      ...prev,
                      ipFilter: opt?.value ?? 'disabled',
                    }));
                  }}
                  onCreateNewProfile={() => onCreateNewProfile({ profileType: 'IP Address Group', page: 'ip-address' })}
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  {`(Up to 64 entries)`}
                </Typography>
              </Box>
            </Box>

            {/* Captive Portal */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Captive Portal</Box>
              <SelectionDropdownProfile
                menuList={captivePortalOptions?.length > 0 ? captivePortalOptions : [{ label: 'Disabled', value: 'disabled' }]}
                value={formData?.captivePortalName ?? 'disabled'}
                width={textfieldStyle.width}
                onChangeItem={(opt) => {
                  setFormData((prev) => ({
                    ...prev,
                    captivePortalName: opt?.value ?? 'disabled',
                  }));
                }}
                onCreateNewProfile={() => onCreateNewProfile({ profileType: 'Captive Portal', page: 'captive-portal' })}
              />
            </Box>
          </Collapse>

          <Box
            sx={{
              margin: "50px auto 30px",
              display: "flex",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            <OutlineButton
              onClick={handleCloseModal}
              width="100px"
              height="32px"
            >
              Cancel
            </OutlineButton>
            <SolidButton
              disabled={isFormInvalid()}
              onClick={checkSaveProRule}
              width="100px"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        </Box>

        <LinkModal
          transparentBackdrop
          open={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          onLinkClick={() => {
            setIsLinkModalOpen(false);
            const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
            navigate(isMspScope ? '/msp/license' : '/license');
          }}
        />
      </ModalContainer>
    </>
  );
};

export default CreateNewWirelessNetworkModal;
